import {Grid, Paper} from "@mui/material"
import ButtonWithIcon from "../../../../components/Button/Button"
import React, {useMemo, useRef, useState} from "react"
import ClearIcon from '@mui/icons-material/Clear'
import {ReactComponent as SearchIcon} from "src/v2/assets/icons/search-icon.svg"
import {Autocomplete} from "@mui/material"
import {dispatchSnackbar} from "../../../../components/SnackBar/snackbar-bus"
import MotorService from "../../../../../services/motor-service"
import PropTypes from "prop-types"
import TextFieldInput from "../../../../components/Input/text-field-input"

const CetipVersaoEventosFilterComponent = ({returnCetipVersao, clearFilter, setCetip, setVersao, disableButtons}) => {
    const service = useMemo(() => new MotorService(), [])
    const [cetipInput, setCetipInput] = useState("")  // texto digitado
    const [cetipValue, setCetipValue] = useState("")  // CETIP selecionado
    const [cetipError, setCetipError] = useState(false)
    const [autoCompleteOptions, setAutoCompleteOptions] = useState([])
    const [autoCompleteLoading, setAutoCompleteLoading] = useState(false)
    const [loadingVersoes, setLoadingVersoes] = useState(false)
    const [versaoDocumentoValue, setVersaoDocumentoValue] = useState("")
    const [versoesDocumentoOptions, setVersoesDocumentoOptions] = useState([])

    const loadCetipSuggestions = (value) => {
        if (value.length < 4) {
            setAutoCompleteOptions([])
            return
        }

        clearTimeout(debounceRef.current)

        debounceRef.current = setTimeout(() => {
            const requestId = ++lastRequestIdRef.current
            setAutoCompleteLoading(true)

            service.loadCadastrosEventos(value, null)
                .then((response) => {
                    if (requestId !== lastRequestIdRef.current) return

                    const cetips = (response.body ?? [])
                        .map(item => item.CETIP)
                        .filter(Boolean)

                    setAutoCompleteOptions([...new Set(cetips)])
                })
                .catch(() => {
                    if (requestId === lastRequestIdRef.current) {
                        setAutoCompleteOptions([])
                    }
                })
                .finally(() => {
                    if (requestId === lastRequestIdRef.current) {
                        setAutoCompleteLoading(false)
                    }
                })
        }, 300)
    }

    const loadVersoesDocumento = (cetipSelecionado) => {
        if (!cetipSelecionado || cetipSelecionado.length < 4) {
            setVersoesDocumentoOptions([])
            setVersaoDocumentoValue("")
            return
        }

        setLoadingVersoes(true)

        service.loadCadastrosEventos(cetipSelecionado)
            .then((response) => {
                const registros = response.body ?? []

                const registrosDoCetipSelecionado = registros.filter(
                    item => item.CETIP === cetipSelecionado
                )

                const versoesUnicas = [
                    ...new Set(
                        registrosDoCetipSelecionado.map(item => item.VersaoDocumento)
                    )
                ]

                setVersoesDocumentoOptions(versoesUnicas)
            })
            .catch(() => {
                setVersoesDocumentoOptions([])
            })
            .finally(() => {
                setLoadingVersoes(false)
            })
    }

    const filterCadastro = () => {
        if ((cetipValue === null || cetipValue === "") && (cetipInput === "" || cetipInput === null)) {
            setCetipError(true)
            dispatchSnackbar("Necessário informar o CETIP", "warning")
            return
        }
        setCetipError(false)
        returnCetipVersao(cetipValue || cetipInput, versaoDocumentoValue)
    }

    const versoesDocumentoSelectOptions = versoesDocumentoOptions.map((versao) => ({
        value: versao,
        label: String(versao),
    }))

    const debounceRef = useRef(null)
    const lastRequestIdRef = useRef(0)

    return <Grid container>
        <Grid item xs={12} xl={10}>
            <Paper sx={{p: disableButtons ? 0 : 2}}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={disableButtons? 6 : 3} xl={3}>
                        <Autocomplete
                            freeSolo
                            options={autoCompleteOptions}
                            loading={autoCompleteLoading}
                            filterOptions={(x) => x}
                            inputValue={cetipInput}
                            value={cetipValue}
                            onInputChange={(event, newInputValue) => {
                                setCetipInput(newInputValue)
                                loadCetipSuggestions(newInputValue)

                                // enquanto digita, limpa versões
                                setVersoesDocumentoOptions([])
                                setVersaoDocumentoValue("")
                                setCetip(newInputValue)
                            }}

                            onChange={(event, newValue) => {
                                if (newValue) {
                                    setCetipValue(newValue)
                                    setCetipInput(newValue)
                                    loadVersoesDocumento(newValue)
                                    setCetip(newValue)
                                }
                            }}
                            renderInput={(params) => (
                                <TextFieldInput
                                    {...params}
                                    label="CETIP"
                                    error={cetipError}
                                    InputLabelProps={{
                                        ...params.InputLabelProps,
                                        shrink: Boolean(cetipInput) || Boolean(cetipValue) || params.InputLabelProps?.shrink,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={disableButtons? 6 : 3} xl={2}>
                        <Autocomplete
                            freeSolo
                            forcePopupIcon={true}
                            disableClearable={false}
                            value={versaoDocumentoValue}
                            options={versoesDocumentoSelectOptions}
                            loading={loadingVersoes}
                            getOptionLabel={(option) => {
                                if (typeof option === "string") return option
                                return option.label || ""
                            }}
                            onChange={(event, newValue) => {
                                // Se newValue for um objeto da lista, pegamos .value,
                                // se for digitação  livre, pegamos o próprio newValue
                                const valorBruto = newValue?.value ?? newValue ?? ""
                                const apenasNumeros = String(valorBruto).replace(/[^0-9]/g, "")
                                setVersaoDocumentoValue(apenasNumeros)
                                setVersao(apenasNumeros)
                            }}
                            onInputChange={(event, newInputValue) => {
                                const apenasNumeros = newInputValue.replace(/[^0-9]/g, "")
                                setVersaoDocumentoValue(apenasNumeros)
                                setVersao(apenasNumeros)
                            }}
                            disabled={loadingVersoes}
                            renderInput={(params) => (
                                <TextFieldInput
                                    {...params}
                                    label="Versão Documento"
                                    InputLabelProps={{
                                        ...params.InputLabelProps,
                                        shrink: Boolean(versaoDocumentoValue) || Boolean(params.inputProps?.value) || params.InputLabelProps?.shrink,
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    {disableButtons ? null :
                        <Grid item xs={12} md={6} container spacing={2} alignItems="center">
                            <Grid item>
                                <ButtonWithIcon
                                    onClick={filterCadastro}
                                    icon={<SearchIcon/>}
                                >
                                    VERIFICAR
                                </ButtonWithIcon>
                            </Grid>

                            <Grid item>
                                <ButtonWithIcon
                                    onClick={() => {
                                        // Limpa os filtros
                                        setCetipInput("")
                                        setCetipValue("")
                                        setVersaoDocumentoValue("")
                                        setVersoesDocumentoOptions([])
                                        setCetip("")
                                        setVersao("")
                                        clearFilter()
                                    }}
                                    icon={<ClearIcon/>}
                                >
                                    LIMPAR FILTRO
                                </ButtonWithIcon>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Paper>
        </Grid>
    </Grid>
}

CetipVersaoEventosFilterComponent.propTypes = {
    returnCetipVersao: PropTypes.func,
    clearFilter: PropTypes.func,
    setCetip: PropTypes.func,
    setVersao: PropTypes.func,
    disableButtons: PropTypes.bool,
}

export default CetipVersaoEventosFilterComponent